import React from 'react';

/*
  Premium funnel palette — vivid spectrum, each stage distinct.
  gradient: string used as CSS background on the bar fill.
*/
const PALETTE = [
  {
    gradient: 'linear-gradient(90deg,#4f46e5 0%,#6366f1 100%)',
    glow:     'rgba(99,102,241,0.3)',
    arrow:    '#6366f1',
    dot:      '#6366f1',
    dotBg:    '#eef2ff',
  },
  {
    gradient: 'linear-gradient(90deg,#7c3aed 0%,#8b5cf6 100%)',
    glow:     'rgba(139,92,246,0.3)',
    arrow:    '#8b5cf6',
    dot:      '#8b5cf6',
    dotBg:    '#f5f3ff',
  },
  {
    gradient: 'linear-gradient(90deg,#0369a1 0%,#0ea5e9 100%)',
    glow:     'rgba(14,165,233,0.3)',
    arrow:    '#0ea5e9',
    dot:      '#0ea5e9',
    dotBg:    '#e0f2fe',
  },
  {
    gradient: 'linear-gradient(90deg,#0d9488 0%,#14b8a6 100%)',
    glow:     'rgba(20,184,166,0.3)',
    arrow:    '#14b8a6',
    dot:      '#14b8a6',
    dotBg:    '#ccfbf1',
  },
  {
    gradient: 'linear-gradient(90deg,#059669 0%,#10b981 100%)',
    glow:     'rgba(16,185,129,0.3)',
    arrow:    '#10b981',
    dot:      '#10b981',
    dotBg:    '#d1fae5',
  },
  {
    gradient: 'linear-gradient(90deg,#d97706 0%,#f59e0b 100%)',
    glow:     'rgba(245,158,11,0.3)',
    arrow:    '#f59e0b',
    dot:      '#f59e0b',
    dotBg:    '#fef3c7',
  },
];

const fmt    = (n) => (n !== undefined && n !== null ? Number(n).toLocaleString('en-IN') : '—');
const fmtPct = (r) => (r !== undefined && r !== null ? `${Number(r).toFixed(2)}%` : '—');

export default function FunnelSection({ funnelData = [] }) {
  const maxEntered = Math.max(...funnelData.map(d => d.entered || 0), 1);

  return (
    <div style={{
      background: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #e8ecf0',
      padding: '20px 22px',
      boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      height: '100%',
      boxSizing: 'border-box'
    }}>

      {/* ── Header ─────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '18px' }}>
        <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', margin: 0 }}>
          Post Approval Funnel
        </h3>
        <span style={{
          width: '15px', height: '15px', borderRadius: '50%',
          border: '1px solid #cbd5e1', display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center',
          fontSize: '9px', color: '#94a3b8', flexShrink: 0
        }}>i</span>
      </div>

      {funnelData.length === 0 ? (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          minHeight: '260px', color: '#94a3b8', fontSize: '13px'
        }}>
          No funnel data available
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>

          {/* ══ LEFT: Funnel shape ══════════════════════════════════════ */}
          <div style={{
            width: '220px',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '3px'
          }}>
            {funnelData.map((item, idx) => {
              const pal = PALETTE[idx % PALETTE.length];
              // Width shrinks proportionally; clamp min 42% so text stays readable
              const widthPct = Math.max(42, (item.entered / maxEntered) * 100);
              const isLast   = idx === funnelData.length - 1;

              return (
                <React.Fragment key={idx}>
                  {/* Funnel bar — centered, narrowing downwards */}
                  <div style={{
                    width:  `${widthPct}%`,
                    height: '38px',
                    background: pal.gradient,
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0 12px',
                    boxShadow: `0 2px 10px ${pal.glow}`,
                    transition: 'transform 0.15s, box-shadow 0.15s',
                    cursor: 'default',
                    boxSizing: 'border-box'
                  }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'scaleX(1.025)';
                      e.currentTarget.style.boxShadow = `0 4px 18px ${pal.glow}`;
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'scaleX(1)';
                      e.currentTarget.style.boxShadow = `0 2px 10px ${pal.glow}`;
                    }}
                  >
                    <span style={{
                      fontSize: '11px', fontWeight: '700', color: '#ffffff',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      maxWidth: '55%', textShadow: '0 1px 3px rgba(0,0,0,0.25)'
                    }}>
                      {item.stage}
                    </span>
                    <span style={{
                      fontSize: '10.5px', fontWeight: '700', color: 'rgba(255,255,255,0.95)',
                      whiteSpace: 'nowrap', flexShrink: 0, textShadow: '0 1px 3px rgba(0,0,0,0.25)'
                    }}>
                      {fmt(item.entered)}&nbsp;({fmtPct(item.conversion)})
                    </span>
                  </div>

                  {/* Connector arrow between bars */}
                  {!isLast && (
                    <div style={{
                      width: '0', height: '0',
                      borderLeft: '11px solid transparent',
                      borderRight: '11px solid transparent',
                      borderTop: `9px solid ${pal.arrow}`,
                      opacity: 0.55,
                      filter: `drop-shadow(0 2px 3px ${pal.glow})`
                    }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {/* ══ RIGHT: Funnel details table ════════════════════════════ */}
          <div style={{ flex: 1, overflowX: 'auto', minWidth: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                  {['Stage', 'Entered', 'Moved Fwd', 'Conversion', 'Drop-off'].map((h, i) => (
                    <th key={i} style={{
                      padding: '7px 10px',
                      textAlign: i === 0 ? 'left' : 'right',
                      fontSize: '11px',
                      fontWeight: '700',
                      color: '#64748b',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      whiteSpace: 'nowrap'
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {funnelData.map((item, idx) => {
                  const pal = PALETTE[idx % PALETTE.length];
                  const hasDropoff = item.dropOff > 0;
                  return (
                    <tr
                      key={idx}
                      style={{ borderBottom: '1px solid #f8fafc', transition: 'background 0.12s' }}
                      onMouseEnter={e => e.currentTarget.style.background = pal.dotBg}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '9px 10px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                          <span style={{
                            width: '9px', height: '9px', borderRadius: '50%',
                            background: pal.dot,
                            boxShadow: `0 0 0 3px ${pal.dotBg}`,
                            flexShrink: 0, display: 'inline-block'
                          }} />
                          <span style={{ fontSize: '12px', fontWeight: '600', color: '#1e293b' }}>
                            {item.stage}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '9px 10px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#334155' }}>
                        {fmt(item.entered)}
                      </td>
                      <td style={{ padding: '9px 10px', textAlign: 'right', fontSize: '12px', color: '#475569' }}>
                        {fmt(item.movedForward)}
                      </td>
                      {/* Conversion — green pill */}
                      <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                        <span style={{
                          display: 'inline-block', fontSize: '11px', fontWeight: '700',
                          color: '#059669', background: '#ecfdf5',
                          border: '1px solid #a7f3d0', borderRadius: '20px',
                          padding: '2px 8px', whiteSpace: 'nowrap'
                        }}>
                          {fmtPct(item.conversion)}
                        </span>
                      </td>
                      {/* Drop-off — red pill or dash */}
                      <td style={{ padding: '9px 10px', textAlign: 'right' }}>
                        {hasDropoff ? (
                          <span style={{
                            display: 'inline-block', fontSize: '11px', fontWeight: '700',
                            color: '#dc2626', background: '#fef2f2',
                            border: '1px solid #fecaca', borderRadius: '20px',
                            padding: '2px 8px', whiteSpace: 'nowrap'
                          }}>
                            {fmt(item.dropOff)} ({fmtPct(item.dropOffPercentage)})
                          </span>
                        ) : (
                          <span style={{ fontSize: '12px', color: '#94a3b8' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

        </div>
      )}
    </div>
  );
}
